import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { breadcrumb } from '../lib/sentry';

/**
 * Magic-link landing. Supabase's JS client auto-parses the URL hash and
 * sets the session when `detectSessionInUrl: true`. We then redirect home
 * (or wherever `?next=...` says).
 */

/**
 * Only accept same-origin paths starting with a single "/" - rejects
 * "//evil.com", "http://evil.com", javascript: schemes, etc.
 */
function safeNextPath(raw: string | null): string | null {
  if (!raw) return null;
  if (!raw.startsWith('/') || raw.startsWith('//')) return null;
  return raw;
}

export default function AuthCallback() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let timeoutId: number | null = null;

    function log(stage: string, extra?: Record<string, unknown>) {
      console.info('[auth-callback]', stage, extra ?? {});
      breadcrumb('auth-callback', stage, extra);
    }

    function done() {
      if (cancelled) return;
      // Honour ?next=/path if it's a safe same-origin path. Password recovery
      // sets next=/reset-password so the user lands on the set-password
      // screen instead of the home feed.
      const dest = safeNextPath(new URL(window.location.href).searchParams.get('next')) ?? '/';
      log('navigating', { dest });
      navigate(dest, { replace: true });
    }

    // 1. Subscribe FIRST so we don't miss the SIGNED_IN event from the
    //    PKCE auto-exchange or from any other in-flight session change.
    log('subscribing');
    const sub = supabase.auth.onAuthStateChange((event, sess) => {
      log('auth-state-change', { event, hasSession: !!sess });
      if (cancelled) return;
      if (sess) {
        sub.data.subscription.unsubscribe();
        if (timeoutId !== null) window.clearTimeout(timeoutId);
        done();
      }
    });

    // 2. Detect PKCE flow: magic link comes back as ?code=... in the
    //    query string. detectSessionInUrl + auto-exchange races against
    //    our own subscription, so call exchangeCodeForSession explicitly.
    const url = new URL(window.location.href);
    const hasCode = url.searchParams.has('code');
    log('inspect-url', { hasCode, hasHashToken: window.location.hash.includes('access_token=') });

    (async () => {
      try {
        if (hasCode) {
          log('exchange-code-start');
          const { data, error: exErr } = await supabase.auth.exchangeCodeForSession(window.location.href);
          if (cancelled) return;
          if (exErr) {
            log('exchange-code-error', { msg: exErr.message });
            sub.data.subscription.unsubscribe();
            if (timeoutId !== null) window.clearTimeout(timeoutId);
            setError(exErr.message);
            return;
          }
          log('exchange-code-success', { hasSession: !!data?.session });
          if (data?.session) {
            sub.data.subscription.unsubscribe();
            if (timeoutId !== null) window.clearTimeout(timeoutId);
            done();
            return;
          }
          // No session yet - fall through to listener + timeout.
        } else {
          // No ?code → implicit/hash flow. Admin-generated invite & magiclink
          // links are ALWAYS implicit: Supabase's /verify endpoint redeems the
          // OTP and redirects back with the session in the URL hash
          // (#access_token=…&refresh_token=…), never as ?code=. A pkce-configured
          // client won't auto-consume that hash via detectSessionInUrl, so we
          // set the session explicitly here - mirroring the exchangeCodeForSession
          // branch above, which likewise doesn't trust auto-detection.
          const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
          const accessToken = hash.get('access_token');
          const refreshToken = hash.get('refresh_token');
          // A genuinely expired/used link (or one a mail scanner pre-fetched and
          // burned) comes back as #error=…&error_description=…. Surface the real
          // reason instead of the generic 10s timeout below.
          const hashError = hash.get('error_description') || hash.get('error');

          if (hashError) {
            log('hash-error', { msg: hashError });
            sub.data.subscription.unsubscribe();
            if (timeoutId !== null) window.clearTimeout(timeoutId);
            setError(hashError);
            return;
          }

          if (accessToken && refreshToken) {
            log('set-session-start');
            const { data, error: ssErr } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken,
            });
            if (cancelled) return;
            if (ssErr) {
              log('set-session-error', { msg: ssErr.message });
              sub.data.subscription.unsubscribe();
              if (timeoutId !== null) window.clearTimeout(timeoutId);
              setError(ssErr.message);
              return;
            }
            log('set-session-success', { hasSession: !!data.session });
            if (data.session) {
              sub.data.subscription.unsubscribe();
              if (timeoutId !== null) window.clearTimeout(timeoutId);
              done();
              return;
            }
          } else {
            // No code, no hash tokens - a refresh or an already-signed-in tab.
            log('get-session-start');
            const { data, error: gsErr } = await supabase.auth.getSession();
            if (cancelled) return;
            if (gsErr) {
              log('get-session-error', { msg: gsErr.message });
              sub.data.subscription.unsubscribe();
              if (timeoutId !== null) window.clearTimeout(timeoutId);
              setError(gsErr.message);
              return;
            }
            log('get-session-result', { hasSession: !!data.session });
            if (data.session) {
              sub.data.subscription.unsubscribe();
              if (timeoutId !== null) window.clearTimeout(timeoutId);
              done();
              return;
            }
          }
        }

        // Still no session - wait for the listener with a 10s safety timeout
        // (was 4s, which fired before slow links could complete).
        timeoutId = window.setTimeout(() => {
          if (cancelled) return;
          log('timeout-fired');
          sub.data.subscription.unsubscribe();
          setError('Link has expired or already been used. Try signing in again.');
        }, 10000);
      } catch (err: any) {
        if (cancelled) return;
        log('unexpected-error', { msg: err?.message });
        sub.data.subscription.unsubscribe();
        if (timeoutId !== null) window.clearTimeout(timeoutId);
        setError(err?.message ?? 'Something went wrong signing you in.');
      }
    })();

    return () => {
      cancelled = true;
      sub.data.subscription.unsubscribe();
      if (timeoutId !== null) window.clearTimeout(timeoutId);
    };
  }, [navigate]);

  return (
    <div style={{
      minHeight: '100dvh',
      display: 'grid',
      placeItems: 'center',
      background: 'var(--soot)',
      color: 'var(--paper)',
      padding: 'var(--space-12) var(--gutter)',
      textAlign: 'center',
      fontFamily: 'var(--f-body)',
    }}>
      {error ? (
        <div>
          <p className="kicker" style={{ color: 'var(--smoke)' }}>Sign in</p>
          <h1 style={{ fontFamily: 'var(--f-display)', fontSize: 'var(--t-2xl)', margin: 'var(--space-4) 0' }}>
            That link didn't work.
          </h1>
          <p style={{ opacity: 0.8 }}>{error}</p>
          <p style={{ marginTop: 'var(--space-6)' }}>
            <a href="/sign-in" style={{ color: 'var(--accent)', textDecoration: 'underline' }}>
              Try again →
            </a>
          </p>
        </div>
      ) : (
        <p>Signing you in…</p>
      )}
    </div>
  );
}
